import React, { useState } from 'react';
import { Video, Upload, Trash2, Youtube, Link2, AlertCircle, CheckCircle } from 'lucide-react';
import {
  getEmbedSrc,
  isEmbedProvider,
  parseVimeoUrl,
  parseYouTubeUrl,
  type VideoSourceType,
} from '../../../utils/videoEmbed';

interface VideoSource {
  sourceType?: VideoSourceType;
  url: string;
  embedUrl?: string;
  cloudfrontUrl?: string;
  mediaId?: string;
  mimeType?: string;
  duration?: number;
}

interface VideoThumbnail {
  url: string;
  cloudfrontUrl?: string;
  mediaId?: string;
  alt?: string;
}

interface VideoData {
  source?: VideoSource;
  thumbnail?: VideoThumbnail;
  [key: string]: any;
}

interface VideoSourceSectionProps {
  data: VideoData;
  setFormData: (data: VideoData) => void;
  openVideoUpload: () => void;
}

export const VideoSourceSection: React.FC<VideoSourceSectionProps> = ({
  data,
  setFormData,
  openVideoUpload,
}) => {
  // For backward compatibility: if there's a URL but no sourceType, treat as 'upload'
  const sourceType = data.source?.sourceType || 'upload';
  // Check if we have an existing uploaded video (even without explicit sourceType)
  const hasUploadedVideo =
    data.source?.url && (sourceType === 'upload' || !data.source?.sourceType);
  const [embedUrlInput, setEmbedUrlInput] = useState(data.source?.embedUrl || '');
  const [embedError, setEmbedError] = useState<string | null>(null);

  const handleRemoveVideo = () => {
    setFormData({
      ...data,
      source: {
        ...data.source,
        url: '',
        embedUrl: undefined,
        cloudfrontUrl: undefined,
        mediaId: undefined,
      },
    });
    setEmbedUrlInput('');
    setEmbedError(null);
  };

  const handleSourceTypeChange = (newType: VideoSourceType) => {
    // Clear existing source when switching types
    setFormData({
      ...data,
      source: {
        sourceType: newType,
        url: '',
        embedUrl: undefined,
        cloudfrontUrl: undefined,
        mediaId: undefined,
        mimeType: newType === 'upload' ? 'video/mp4' : undefined,
      },
    });
    setEmbedUrlInput('');
    setEmbedError(null);
  };

  const handleEmbedUrlChange = (url: string) => {
    setEmbedUrlInput(url);
    setEmbedError(null);

    if (!url.trim()) {
      setFormData({
        ...data,
        source: {
          ...data.source,
          sourceType,
          url: '',
          embedUrl: undefined,
        },
      });
      return;
    }

    // Parse the URL based on source type
    let videoId: string | null = null;

    if (sourceType === 'youtube') {
      videoId = parseYouTubeUrl(url);
      if (!videoId) {
        setEmbedError('Invalid YouTube URL. Please use a valid YouTube video link.');
        return;
      }
    } else if (sourceType === 'vimeo') {
      videoId = parseVimeoUrl(url);
      if (!videoId) {
        setEmbedError('Invalid Vimeo URL. Please use a valid Vimeo video link.');
        return;
      }
    }

    if (videoId) {
      setFormData({
        ...data,
        source: {
          ...data.source,
          sourceType,
          url: videoId,
          embedUrl: url,
        },
      });
    }
  };

  // Get display info for the current embed
  const getEmbedDisplayInfo = () => {
    if (isEmbedProvider(sourceType) && data.source?.url) {
      const previewUrl = getEmbedSrc(sourceType, data.source.url);
      if (!previewUrl) return null;
      return {
        icon:
          sourceType === 'youtube' ? (
            <Youtube size={16} className="text-red-600" />
          ) : (
            <Video size={16} className="text-blue-500" />
          ),
        label: sourceType === 'youtube' ? 'YouTube Video' : 'Vimeo Video',
        previewUrl,
      };
    }
    return null;
  };

  const embedInfo = getEmbedDisplayInfo();

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Video Source</h3>
      <div className="space-y-4">
        {/* Source Type Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Source Type</label>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => handleSourceTypeChange('upload')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                sourceType === 'upload'
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
            >
              <Upload size={18} />
              <span className="font-medium">Upload</span>
            </button>
            <button
              type="button"
              onClick={() => handleSourceTypeChange('youtube')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                sourceType === 'youtube'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
            >
              <Youtube size={18} />
              <span className="font-medium">YouTube</span>
            </button>
            <button
              type="button"
              onClick={() => handleSourceTypeChange('vimeo')}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                sourceType === 'vimeo'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
            >
              <Video size={18} />
              <span className="font-medium">Vimeo</span>
            </button>
          </div>
        </div>

        {/* Upload Source */}
        {sourceType === 'upload' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video <span className="text-red-500">*</span>
            </label>

            {hasUploadedVideo ? (
              <div className="space-y-3">
                {/* Video Preview */}
                <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 group">
                  <div className="aspect-video">
                    <video
                      src={data.source?.cloudfrontUrl || data.source?.url}
                      className="w-full h-full object-cover"
                      controls
                      poster={data.thumbnail?.cloudfrontUrl || data.thumbnail?.url}
                    />
                  </div>

                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={openVideoUpload}
                        className="px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium"
                      >
                        <Upload size={16} />
                        <span>Replace</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveVideo}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 flex items-center space-x-2 text-sm font-medium"
                      >
                        <Trash2 size={16} />
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Video info */}
                <div className="text-sm text-gray-600 flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="flex items-center space-x-2">
                    <Video size={16} className="text-purple-600" />
                    <span className="font-medium">Uploaded video</span>
                  </span>
                  <button
                    type="button"
                    onClick={openVideoUpload}
                    className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
                  >
                    Change video
                  </button>
                </div>
              </div>
            ) : (
              /* Upload placeholder */
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 cursor-pointer group"
                onClick={openVideoUpload}
              >
                <div className="flex flex-col items-center space-y-4">
                  <div className="p-4 bg-gray-100 group-hover:bg-primary-100 rounded-full transition-colors">
                    <Video size={32} className="text-gray-600 group-hover:text-primary-600" />
                  </div>
                  <div>
                    <p className="text-base font-medium text-gray-700 group-hover:text-primary-700">
                      Upload Video
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Click to select or drag and drop your video
                    </p>
                    <p className="text-xs text-gray-400 mt-2">MP4, WebM, or OGV up to 50MB</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* YouTube/Vimeo Embed Source */}
        {(sourceType === 'youtube' || sourceType === 'vimeo') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {sourceType === 'youtube' ? 'YouTube' : 'Vimeo'} Video URL{' '}
              <span className="text-red-500">*</span>
            </label>

            {/* URL Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Link2 size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={embedUrlInput}
                onChange={e => handleEmbedUrlChange(e.target.value)}
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-offset-0 transition-colors ${
                  embedError
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : data.source?.url
                      ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                      : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                }`}
                placeholder={
                  sourceType === 'youtube'
                    ? 'https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID'
                    : 'https://vimeo.com/VIDEO_ID'
                }
              />
              {/* Status indicator */}
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                {embedError && <AlertCircle size={18} className="text-red-500" />}
                {!embedError && data.source?.url && (
                  <CheckCircle size={18} className="text-green-500" />
                )}
              </div>
            </div>

            {/* Error message */}
            {embedError && <p className="mt-2 text-sm text-red-600">{embedError}</p>}

            {/* Help text */}
            {!embedError && !data.source?.url && (
              <p className="mt-2 text-sm text-gray-500">
                {sourceType === 'youtube'
                  ? 'Paste a YouTube video URL (e.g., youtube.com/watch?v=... or youtu.be/...)'
                  : 'Paste a Vimeo video URL (e.g., vimeo.com/123456789)'}
              </p>
            )}

            {/* Preview for valid embed */}
            {!embedError && data.source?.url && embedInfo && (
              <div className="mt-4 space-y-3">
                {/* Video Preview */}
                <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-black">
                  <div className="aspect-video">
                    <iframe
                      src={embedInfo.previewUrl}
                      className="w-full h-full"
                      style={{ border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Video preview"
                    />
                  </div>

                  {/* Overlay with remove action */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
                    <button
                      type="button"
                      onClick={handleRemoveVideo}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 flex items-center space-x-2 text-sm font-medium"
                    >
                      <Trash2 size={16} />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>

                {/* Video info */}
                <div className="text-sm text-gray-600 flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="flex items-center space-x-2">
                    {embedInfo.icon}
                    <span className="font-medium">{embedInfo.label}</span>
                    <span className="text-gray-400">ID: {data.source.url}</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
